/**
 * WPQ LINKING COMPONENT (NON-NPCIL PROJECTS)
 * 
 * PURPOSE:
 * Links Welder Performance Qualification (WPQ) records with standard project joints
 * for welding compliance and traceability in non-nuclear projects.
 * 
 * DYNAMODB STRUCTURE:
 * 
 * 1. WI_mst Table - Main WPQ Linking Records:
 *    PK: "WPQ_Linking"
 *    SK: "{ProjectNo}#{JointType}"
 *    Attributes:
 *    - WI_Project_No: Project identifier
 *    - WI_Joint_No: Joint number (single joint, not comma-separated)
 *    - WI_Joint_Type: "Groove", "Overlay", or "Tubesheet"
 *    - WI_Welder_Id: Qualified welder identifiers (display only)
 *    - WI_Wpq_Radio: "Link WPQ with Project" or "Linking WPQ with project not Required"
 *    - Action: "Add" or "Edit"
 * 
 * 2. WI_mst Table - WPQ Association Records (Chunked):
 *    PK: "WPQ_Linking"
 *    SK: "{ProjectNo}#{JointType}#WPQ#{ChunkNumber}"
 *    Attributes:
 *    - WI_WPQ_Data: Comma-separated WPQ identifiers (up to 200 per chunk)
 *    Format: "W001#WPQ123,W002#WPQ456,W003#WPQ789,..."
 * 
 * 3. WPQ_HEADER Table (Referenced):
 *    PK: "WPQ_HEADER"
 *    SK: "{WelderID}#{WPQRecordNo}"
 *    Contains welder qualification details
 * 
 * 4. WCS_HEADER Table (Referenced):
 *    PK: "WCS_HEADER"
 *    SK: "{ProjectNo}#{JointNo}"
 *    Contains welding consumable specifications and joint details
 * 
 * BUSINESS LOGIC:
 * 
 * 1. PROJECT-JOINT TYPE COMBINATION:
 *    - Each project+joint_type combination = single main record
 *    - SK pattern: "ProjectNo#JointType" (simpler than NPCIL)
 *    - No sequence numbering (unlike NPCIL which supports multiple teams)
 *    - One-to-one mapping between project-joint_type and WPQ set
 * 
 * 2. WPQ CHUNKING STRATEGY:
 *    - Same chunking logic as NPCIL (200 WPQs per chunk)
 *    - Prevents DynamoDB item size limits
 *    - Enables unlimited WPQ associations per project
 * 
 * 3. JOINT SELECTION:
 *    - Tubesheet: Requires specific joint number selection
 *    - Groove/Overlay: Joint number optional (project-level linking)
 *    - Joint data sourced from WCS_HEADER table
 * 
 * 4. DUPLICATE PREVENTION:
 *    - Checks for existing Project+JointType combinations
 *    - Returns "Duplicate" message if combination exists
 *    - Prevents multiple WPQ sets for same project-joint_type
 * 
 * INTERFACE OPERATIONS:
 * - Add: Create new WPQ-project linkages
 * - Edit: Modify existing linkages
 * - View: Display linked WPQ records in grid
 * - Validation: Enforce business rules and required fields
 * 
 * KEY DIFFERENCES FROM NPCIL:
 * - Simpler SK pattern (no sequence numbers)
 * - Single WPQ set per project-joint_type
 * - Optional joint numbers for most joint types
 * - No conflict resolution (duplicate prevention only)
 * - Standard project compliance (non-nuclear)
 */

import { useState, useRef, forwardRef, useEffect } from "react";
import { Row, Col, Container, Form, Button } from "react-bootstrap";
import GlobalColumnDefinations, { getHeader_FieldName } from "../utility/grid/definitions/AgGridDefination";
import { showSuccess, showWarning } from "../utility/ui/notifications/Toaster";
import { addwpqlink, addNpcilWpqLink, deleteNpcilWpqLink, getWpqNpcilLinkByProjectNumber } from "../../Services/WI-services";
import { getAllProjectsByFlag } from "../../Services/project-services";
import { isAlphanumericWithDot } from "../../functions/validations";
import { BiSelectMultiple } from "react-icons/bi";
import { RenderIf } from "../../functions/welding-functions";
import GridMaster from "../utility/grid/GridMaster";
import MultiValuePopUp from "../utility/ui/modals/MultiValuePopUp";
import { Action } from "../utility/common/constants/CommonConstants"
import { useLocation } from "react-router-dom";
import { getWCSDataHeader } from "../../Services/wcs-services";
import { SearchSelect } from "../utility/forms/inputs/search-select";
import { getLinkedWPQRecords, addWPQLinkingData, getWPQLinkingData } from "./WPQLinkingCommon";
import { GridLoader } from "react-spinners";
import { Jointstatus } from "../utility/common/TransactionComponents/TransactionEntity";

const WpqLinkingAddEdit = forwardRef((props) => {
  const npcilflag = false;
  const [reload, setReload] = useState(false);
  const [projectDetails, setProjectDetails] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [columnDefs, setColumnDefs] = useState([]);
  const [wcsprojectdata, setwcsprojectdata] = useState([]);
  const childRef = useRef();
  const location = useLocation();
  const action = location.state?.action;
  const [jointNumbers, setJointNumbers] = useState([]);
  const [filteredResult, setFilteredResult] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedWPQData, setSelectedWPQData] = useState([]);

  /**
   * WPQ LINKING DATA STRUCTURE (NON-NPCIL)
   * 
   * MAIN RECORD FORMAT:
   * {
   *   PK: "WPQ_Linking",                     // Partition Key
   *   SK: "PROJECT2024#Groove",              // Sort Key Pattern
   *   WI_Project_No: "PROJECT2024",          // Project identifier
   *   WI_Joint_No: "J001",                   // Single joint (optional for most types)
   *   WI_Joint_Type: "Groove",               // Joint classification
   *   WI_Welder_Id: "W001,W002",             // Display only (comma-separated)
   *   WI_Wpq_Radio: "Link WPQ with Project", // Linking requirement
   *   Action: "Add",                         // Operation type
   *   WI_WCS_Data: [],                       // WCS reference data
   *   WCS_Project_No: "PROJECT2024"          // WCS project reference
   * }
   * 
   * WPQ ASSOCIATION RECORDS (CHUNKED):
   * {
   *   PK: "WPQ_Linking",
   *   SK: "PROJECT2024#Groove#WPQ#1",        // Chunk 1
   *   WI_WPQ_Data: "W001#WPQ123,W002#WPQ456" // Up to 200 WPQs
   * }
   */
  const [wiObj, setWiObj] = useState(
    action === "Edit" ? location.state?.data
      : {
        PK: "WPQ_Linking",
        SK: "",
        SR_No: "",
        Action: "",
        WI_Project_No: "",
        WI_Joint_No: "",
        WI_WCS_Data: [],
        WI_Welder_Id: "",
        WI_FileFormat: "",
        WI_Wpq_Radio: "",
        WI_Joint_Type: "",
        WCS_Project_No: "",
        WI_WPQ_Data: "",
      }
  );

  let tmpcolumnDefs;
  tmpcolumnDefs = [
    {
      headerName: "Welder Id",
      field: "WPQ_WelderId",
      type: "Text",
      isCheckbox: false,
      cellRenderer: "",
    },
    {
      headerName: "Welder Name",
      field: "WPQ_WelderName",
      type: "Text",
      isCheckbox: false,
      cellRenderer: "",
    },
    {
      headerName: "WPQ Number",
      field: "WPQ_RecordNo",
      type: "Text",
      isCheckbox: false,
      cellRenderer: "",
    }
  ];

  const headerName = (field) => {
    let name = getHeader_FieldName(field, tmpcolumnDefs, "getHeader");
    return name;
  };

  const fieldName = (header) => {
    let name = getHeader_FieldName(header, tmpcolumnDefs, "getField");
    return name;
  };

  const [error, setError] = useState({
    WI_Project_No: {},
    WI_Joint_No: {},
    WI_Welder_Id: {},
    WI_From_Date: {},
    WI_To_Date: {},
    WI_Slip_Status: {},
  });

  const WPQ_Linking_Radio = {
    Linked: "Link WPQ with Project",
    UnLinked: "Linking WPQ with project not Required",
  };

  const fetchProject = async () => {
    let result = await getAllProjectsByFlag(npcilflag);
    setProjectDetails(result);
  };

  const fetchWPQLinkingByPKSK = async () => {
    setLoading(true);
    let result = [];
    result.push(location.state?.data);
    let linkedWpq = await getWPQLinkingData(result[0]);
    let linkedData = await getLinkedWPQRecords(linkedWpq.join(","), result[0].WI_Joint_Type);
    if (linkedData) {
      setFilteredResult(linkedData);
    }
    setLoading(false);
    setWiObj(result[0]);
  }

  useEffect(() => {
    if (action === "Edit") {
      setLoading(true);
      fetchWPQLinkingByPKSK();
    }
    fetchProject();
    fetchColumnDefns();
    fetchJoints();
  }, []);

  useEffect(() => {
    if (reload) {
      setReload(false);
    }
  }, [reload]); // Fetch widata and reset reload when reload changes

  const fetchJoints = async () => {
    if (action === "Edit") {
      let result = await getWCSDataHeader({
        WCS_Project_No: wiObj?.WI_Project_No,
      });
      if (wiObj.WI_Project_No && result.length > 0) {
        const selectedJointNos = result[1]?.filter(item => item.WCS_Project_No === wiObj.WI_Project_No);
        if (selectedJointNos.length > 0) {
          const formattedJointNos = selectedJointNos.map(item => ({
            "WI_Joint_No": item.WCS_Joint_No
          }));
          setJointNumbers(formattedJointNos);
        }
      }
      setwcsprojectdata(result);
    }
  };

  const validate = async () => {
    let isError = false;
    let emptyFields = [];
    if (wiObj.WI_Project_No === "") {
      isError = true;
      emptyFields.push("Project No");
    }
    if (wiObj.WI_Joint_Type === "") {
      isError = true;
      emptyFields.push("Joint Type");
    }
    if (wiObj.WI_Joint_Type === "Tubesheet") {
      if (wiObj.WI_Joint_No === "") {
        isError = true;
        emptyFields.push("Joint No");
      }
    }
    if (wiObj.WI_Wpq_Radio === WPQ_Linking_Radio.Linked) {
      if (wiObj.WI_Welder_Id === "") {
        isError = true;
        emptyFields.push("Welder Id");
      }
    }
    if (isError) {
      showWarning(`Field Required: ${emptyFields.join(", ")}`);
    }
    setError((prevState) => ({
      ...prevState,
      ...error,
    }));
    if (error.WI_Project_No.status) {
      isError = true;
    }
    return isError;
  };

  /**
   * WPQ LINKING SUBMISSION LOGIC (NON-NPCIL)
   * 
   * PROCESS FLOW:
   * 1. Validate required fields based on joint type
   * 2. Generate simple SK: "ProjectNo#JointType"
   * 3. Save main linking record
   * 4. Save WPQ associations with chunking (if not duplicate)
   * 5. Handle duplicate detection and user feedback
   * 
   * SK GENERATION:
   * - Simple pattern: "{ProjectNo}#{JointType}"
   * - No sequence numbers (unlike NPCIL)
   * - One record per project-joint_type combination
   * 
   * DUPLICATE HANDLING:
   * - Backend checks for existing Project+JointType combination
   * - Returns "Duplicate" message if exists
   * - Prevents WPQ association creation on duplicates
   * - Shows appropriate warning to user
   * 
   * WPQ CHUNKING:
   * - Uses same addWPQLinkingData() function as NPCIL
   * - Processes selectedWPQData in 200-record chunks
   * - Creates multiple WPQ records with incremented chunk numbers
   */
  const handleSubmitTubesheet = async () => {
    try {
      wiObj.Action = action;
      wiObj.PK = "WPQ_Linking";

      let wpqJointCombinationsResult = await getWpqNpcilLinkByProjectNumber({ projectNumber: wiObj.WI_Project_No, PK: "WPQ_Linking" });
      // Filter only Tubesheet records for this PK
      wpqJointCombinationsResult = wpqJointCombinationsResult.filter(
        ele => ele.PK === "WPQ_Linking" && ele.WI_Joint_Type === "Tubesheet"
      );

      let maxSeqNo = Math.max(...wpqJointCombinationsResult.map(ele => ele.SeqNo || 1), 1);
      let matchedEntry = null;
      let newObj = {};
      const jointNos = wiObj.WI_Joint_No.split(",");

      // SMART GROUPING - Find compatible existing combination with same WPQ set
      for (let ele of wpqJointCombinationsResult) {
        if (ele.SeqNo === wiObj.SeqNo) continue;  // skip self
        const eleJoints = ele.WI_Joint_No.split(",");
        const eleWpqs = await getWPQLinkingData(ele);

        let isWelderMatch;
        if (selectedWPQData.length !== 0) {
          isWelderMatch = selectedWPQData.length === eleWpqs.length &&
            selectedWPQData.every(id => eleWpqs.includes(id));
        } else {
          isWelderMatch = true;
        }

        if (isWelderMatch && ele.WI_Joint_Type === wiObj.WI_Joint_Type) {
          matchedEntry = { ...ele, eleJoints };
          break;
        }
      }

      if (action === "Edit" && matchedEntry) {
        if (matchedEntry.SeqNo !== wiObj.SeqNo) {
          const jointsToAdd = matchedEntry.eleJoints.filter(joint => !jointNos.includes(joint));
          if (jointsToAdd.length > 0) {
            wiObj.WI_Joint_No = [...jointNos, ...jointsToAdd].join(',');
          }
          await deleteNpcilWpqLink(matchedEntry.PK, matchedEntry.SK);
        }
        newObj = { ...wiObj };
      } else if (action === "Add") {
        if (matchedEntry && matchedEntry.SeqNo !== wiObj.SeqNo) {
          const jointsToAdd = matchedEntry.eleJoints.filter(joint => !jointNos.includes(joint));
          if (jointsToAdd.length > 0) {
            wiObj.WI_Joint_No = [...jointNos, ...jointsToAdd].join(',');
          }
          newObj = { ...wiObj, SeqNo: matchedEntry.SeqNo, SK: matchedEntry.SK };
        } else {
          let newSeqNo = wpqJointCombinationsResult.length === 0 ? 1 : maxSeqNo + 1;
          newObj = {
            ...wiObj,
            SeqNo: newSeqNo,
            SK: `${wiObj.WI_Project_No}#${wiObj.WI_Joint_Type}#SeqNo#${newSeqNo}`
          };
        }
      }

      // FALLBACK
      if (Object.keys(newObj).length === 0) {
        newObj = { ...wiObj };
        if (newObj.SK === "" && wpqJointCombinationsResult.length === 0) {
          newObj.SeqNo = maxSeqNo;
          newObj.SK = `${wiObj.WI_Project_No}#${wiObj.WI_Joint_Type}#SeqNo#${maxSeqNo}`;
        }
      }

      newObj.WI_WCS_Data = "";
      delete newObj.WI_WPQ_Data;
      let deleteLinksFlg = action === "Edit";

      let result = await addNpcilWpqLink(newObj, deleteLinksFlg);
      if (result.status) {
        await addWPQLinkingData(selectedWPQData, newObj);
        showSuccess(`Selected WPQ Linking has been ${action === "Edit" ? "Updated" : "Added"} successfully`);
        props.setReload(!props.reload);
        setShowForm(false);
        props.onHide();
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const handleSubmitDefault = async () => {
    try {
      wiObj.Action = action;
      let resp;

      wiObj.SK = wiObj.WI_Project_No + "#" + wiObj.WI_Joint_Type;
      delete wiObj.WI_WPQ_Data;

      resp = await addwpqlink(wiObj);

      if (resp.message !== "Duplicate") {
        await addWPQLinkingData(selectedWPQData, wiObj);
      }

      if (action === "Edit") {
        showSuccess("Selected WPQ Linking has been Updated successfully");
      } else if (resp.message === "Duplicate") {
        showWarning("Combination of Project number and Joint Type already exist");
      } else if (action === "Add") {
        showSuccess("Selected WPQ Linking has been Added successfully");
      }

      props.setReload(!props.reload);
      setShowForm(false);
      props.onHide();
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const handleSubmit = async (e) => {
    try {
      if (!(await validate())) {
        if (wiObj.WI_Joint_Type === "Tubesheet") {
          await handleSubmitTubesheet();
        } else {
          await handleSubmitDefault();
        }
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const handleChangeselect = (e) => {
    const { name, value } = e.target;
    setWiObj((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const [popUp, setPopUp] = useState({
    name: "",
    type: "",
    key: "",
    value: "",
  });

  const showPopUp = (name, type, key, value) => {
    setPopUp({ name, type, key, value });
    setShowForm(true);
  };

  const setPopUpData = (data) => {
    if (data.name === "WPQ") {
      setFilteredResult(data.result);
      let skValuesArr = [];
      let welderIdsArr = [];
      //Welder ID and WPQ Rercord Number combined ==> get WPQ Header SK
      data.result.forEach(element => {
        skValuesArr.push(element.WPQ_WelderId + "#" + element.WPQ_RecordNo);
        welderIdsArr.push(element.WPQ_WelderId);
      });
      let welderValues = welderIdsArr.join(",");
      setSelectedWPQData(skValuesArr);
      setWiObj((prevState) => ({
        ...prevState,
        WI_Welder_Id: welderValues,
      }));
    }
    else if (data.name === "NPCIL") {
      let arr = data.result.map((e) => e.WCS_Joint_No);
      setWiObj((prevState) => ({
        ...prevState,
        WI_Joint_No: arr.join(",")
      }));
      setShowForm(false);
    }
  };

  const fetchColumnDefns = async () => {
    const result = GlobalColumnDefinations(tmpcolumnDefs);
    setColumnDefs(result);
  };

  const handleCheckboxClick = (e) => {
    const selectedValue = e.target.value;
    setWiObj((prev) => {
      const updatedObj = {
        ...prev,
        WI_Wpq_Radio: selectedValue,
        WI_WPQ_Data: "",
        WI_Welder_Id: "",
      };
      if (selectedValue !== "Link WPQ with Project" && selectedValue !== "Linking WPQ with project not Required") {
        updatedObj.WI_Joint_Type = selectedValue;
        updatedObj.WI_Wpq_Radio = "Link WPQ with Project";
      }
      return updatedObj;
    });
  };

  const handleJointTypeChange = async (e) => {
    let value = e.value;
    setWiObj((prev) => ({
      ...prev,
      WI_Joint_Type: value,
      WI_Wpq_Radio:
        (value === "Tubesheet")
          ? "Link WPQ with Project"
          : "Linking WPQ with project not Required",
      WI_Joint_No: "",
      WI_Welder_Id: "",
    }));

    setFilteredResult([]);
    wiObj.WI_WPQ_Data = "";

    if (wiObj.WI_Project_No && wcsprojectdata && wcsprojectdata.length > 0 && !npcilflag) {
      let selectedJointNos = wcsprojectdata[1]?.filter(item => item.WCS_Project_No === wiObj.WI_Project_No);
      const formattedJointNos = selectedJointNos.map(item => ({
        "WI_Joint_No": item.WCS_Joint_No
      }));
      setJointNumbers(formattedJointNos);
    }
  }

  const handleProjectChange = async (e) => {
    let value = e.value;
    setWiObj((prev) => ({
      ...prev,
      WI_Project_No: value,
      WCS_Project_No: value,
      WI_Joint_No: "",
      WI_Joint_Type: "",
      ...(npcilflag && { WI_Wpq_Radio: "Link WPQ with Project" })
    }));

    if (action === "Add") {
      const result = await getWCSDataHeader({
        WCS_Project_No: value,
      });
      setwcsprojectdata(result);
    }
  }

  return (
    <>
      {loading && (
        <div className="loader-overlay">
          <GridLoader size={10} className="pageloader" />
        </div>
      )}
      <div className="position-absolute ms-3" style={{ top: "15px" }}>
        <b style={{ fontSize: "22px" }}>{"WPQ-Linking"}</b>
      </div>
      <div className="location">
        <div className="ltext" style={{ position: "relative", left: "7px" }}>
          {" "}
          Home
          {"> "}
          <span>{"Welder and Welding Operations"}</span> {"> "}
          <span>{"WPQ-Linking"}</span>
        </div>
      </div>
      <Container fluid>
        <Row className="RowHeight">
          <Col md={4} sm={12} xs={12} className="ListTitle mt-2 mb-4"
            style={{ position: "relative", left: "7px" }}>
            Add New Project
          </Col>
        </Row>
        <div className="px-2 pb-2 mt-2 ">
          <Row className="gx-3" style={{ height: "40px" }}>
            <Col md={3}>
              <Form.Group className="promd4-input-selectNpcil">
                <div className="search-select">
                  <SearchSelect
                    id="WI_Project_No"
                    name="WI_Project_No"
                    data={projectDetails}
                    required
                    disabled={action === "Edit"}
                    valueField={"wld_ProjectNumber"}
                    value={wiObj.WI_Project_No}
                    labelField={"wld_ProjectNumber"}
                    placeholder=""
                    onChange={handleProjectChange}
                  ></SearchSelect>
                </div>
                <Form.Label>Project Number</Form.Label>
              </Form.Group>
            </Col>
          </Row>
          <div style={{ height: "14px" }}></div>
          <Row className="gx-3" style={{ height: "40px" }}>
            <Col md={3}>
              <Form.Group className="promd4-input-selectNpcil">
                <div className="search-select">
                  <SearchSelect
                    id="WI_Joint_Type"
                    name="WI_Joint_Type"
                    data={Jointstatus}
                    required
                    disabled={action === "Edit"}
                    valueField={"value"}
                    value={wiObj.WI_Joint_Type}
                    labelField={"name"}
                    placeholder=""
                    onChange={handleJointTypeChange}
                  ></SearchSelect>
                </div>
                <Form.Label>Joint Type</Form.Label>
              </Form.Group>
            </Col>
          </Row>
          <div style={{ height: "14px" }}></div>
          <Row className="gx-3" style={{ height: "40px" }}>
            <Col md={3}>
              <Form.Group className="g-input">
                <div className="d-flex align-items-center">
                  <Form.Control
                    id="WI_Joint_No"
                    type="text"
                    name="WI_Joint_No"
                    maxLength="100"
                    placeholder=" "
                    disabled={wiObj.WI_Joint_Type !== "Tubesheet"}
                    readOnly
                    value={wiObj.WI_Joint_No}
                    onChange={handleChangeselect}
                    className="rightPaddingNpcil"
                    onInput={(e) => {
                      let error = isAlphanumericWithDot(e.target.value);
                      setError((prevState) => ({
                        ...prevState,
                        SK: error
                      }));
                    }}
                  />
                  <Form.Label className="required-field" htmlFor="WI_Joint_No">
                    Select Joint Number
                  </Form.Label>
                  <BiSelectMultiple
                    size={20}
                    onClick={() => {
                      if (wiObj.WI_Joint_Type == "Tubesheet") {
                        if (!wiObj.WI_Joint_Type) {
                          showWarning("Please select Joint Type first");
                          return;
                        }
                        showPopUp("NPCIL",
                          "Multiple",
                          "WI_Joint_No", {
                          "projectNumber": wiObj.WI_Project_No,
                          "action": action
                        });
                      }
                    }}
                    color={"#707070"}
                    style={{ marginLeft: "-2rem" }}
                  />
                </div>
              </Form.Group>
            </Col>
          </Row>
          <Row className="gx-3" style={{ marginBottom: "2rem", height: "40px" }}>
            <Col md={2}>
              <div>
                <input
                  type="radio"
                  name="WI_Wpq_Radio"
                  value="Link WPQ with Project"
                  onClick={(e) => handleCheckboxClick(e)}
                  onChange={(e) => handleCheckboxClick(e)}
                  style={{ position: "relative", top: "2px" }}
                  checked={wiObj.WI_Wpq_Radio === WPQ_Linking_Radio.Linked}
                />
                <span style={{ fontSize: "14px" }}>
                  &nbsp; Link WPQ with Project
                </span>
              </div>
            </Col>
            <Col md={3}>
              <div style={{ width: "200px", display: "inline-block", lineHeight: "1.2" }}>
                <input
                  disabled={wiObj.WI_Joint_Type === "Tubesheet"}
                  type="radio"
                  name="WI_Wpq_Radio"
                  value="Linking WPQ with project not Required"
                  onClick={(e) => handleCheckboxClick(e)}
                  onChange={(e) => handleCheckboxClick(e)}
                  style={{ position: "relative", top: "2px" }}
                  checked={wiObj.WI_Wpq_Radio === WPQ_Linking_Radio.UnLinked}
                />
                <span style={{ fontSize: "14px" }}>
                  &nbsp; Linking WPQ with project
                </span>
                <span style={{
                  fontSize: "14px",
                  position: "relative",
                  left: "20px",
                }}
                >
                  &nbsp; not Required
                </span>
              </div>
            </Col>
          </Row>
          <Row className="gx-3" style={{ marginBottom: "2rem" }}>
            <Col md={3} style={{ position: "relative", bottom: "20px" }}>
              <Form.Group className="g-input">
                <div className="d-flex align-items-center">
                  <Form.Control
                    id="WI_Welder_Id"
                    name="WI_Welder_Id"
                    type="text"
                    required
                    disabled={wiObj.WI_Wpq_Radio === WPQ_Linking_Radio.UnLinked}
                    placeholder=" "
                    color="var(--col-707070)"
                    value={wiObj.WI_Welder_Id}
                    className="rightPaddingNpcil"
                    onChange={handleChangeselect}
                    readOnly
                    onInput={(e) => {
                      let error = isAlphanumericWithDot(e.target.value);
                      setError((prevState) => ({
                        ...prevState,
                        SK: error,
                      }));
                    }}
                  />
                  <Form.Label className="required-field" htmlFor="WI_Welder_Id">
                    Select WPQ
                  </Form.Label>
                  <BiSelectMultiple
                    size={20}
                    onClick={() => {
                      if (wiObj.WI_Wpq_Radio !== WPQ_Linking_Radio.UnLinked) {
                        if (!wiObj.WI_Joint_Type) {
                          showWarning("Please select Joint Type first");
                          return;
                        }
                        showPopUp(
                          "WPQ",
                          "Multiple",
                          "WI_Welder_Id",
                          wiObj.WI_Welder_Id
                        );
                      }
                    }}
                    color={"#707070"}
                    style={{ marginLeft: "-2rem" }}
                  />
                </div>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col>
              <Button
                onClick={handleSubmit}
                style={{ position: "relative", bottom: "30px" }}
                className="btn-add-color semiBoldToggleButton leftAlignedButton"
              >
                {action === "Edit" ? "Update" : "Add"}
              </Button>
            </Col>
          </Row>
          {filteredResult.length > 0 || action === Action.ActionEdit ? (
            <div style={{
              position: "relative",
              left: "500px",
              bottom: "420px",
              width: "580px",
              height: "483px",
              border: "1px solid var(--col-ddd)"
            }}
            >
              <GridMaster
                rowDetails={filteredResult}
                colDetails={columnDefs}
                fieldNames={fieldName}
                headerNames={headerName}
                ref={childRef}
                height={"99%"}
              />
            </div>
          ) : (
            ""
          )}
        </div>
        <RenderIf isTrue={popUp.type === "Multiple" && showForm}>
          <MultiValuePopUp
            show={showForm}
            onHide={() => setShowForm(false)}
            setSelected={setPopUpData}
            masterName={popUp.name}
            paramkey={popUp.key}
            paramvalue={popUp.value}
            backdrop="static"
            JointType={wiObj.WI_Joint_Type}
            wiobj={wiObj}
            filteredResult={filteredResult}
            action={action}
          />
        </RenderIf>
      </Container >

    </>
  );
});

export default WpqLinkingAddEdit;